import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import type {
  AddShape,
  BankOption,
  ConnectedCollection,
  ConnectionRow,
  ConsentSource,
  ImportRecord,
  SourceBase,
  SourceCheck,
  SourceState as WireState,
} from '@/api/contract'
import { apiFetch } from '~/api/client'
import { useRememberCollection } from '~/state/collections'

export type {
  AddField,
  AddKind,
  AddShape,
  BankOption,
  ConnectedCollection,
  CollectionEntry,
  CollectionItem,
  CollectionSource,
  Consent,
  ConsentAccount,
  ConsentHolder,
  ConsentSource,
  ConnectionRow,
  CredentialsSource,
  EntityOption,
  ImportRecord,
  PendingAccount,
  PendingConsent,
  SourceCheck,
  SourceFieldState as SourceField,
} from '@/api/contract'

/**
 * A source this build has no renderer for. Kept as a card that says so, because
 * a tab left open across a deploy that adds a kind would otherwise take the
 * whole page down — and this is the one page whose reader cannot read a console.
 */
export interface UnknownSource extends SourceBase {
  kind: 'unknown'
  reportedKind: string
}

export type SourceState = WireState | UnknownSource

export interface SourcesResponse {
  sources: SourceState[]
}

/** A source as it arrives, before this build has decided it understands it. */
export interface WireSource extends SourceBase {
  kind?: string
  [key: string]: unknown
}

const SOURCES_KEY = ['sources'] as const

/**
 * A source with no kind is the shape the API sent before kinds existed, and a
 * kind this build has never heard of is a tab left open across a deploy.
 * Reading the absence as credentials is what lets page and server land in
 * either order.
 */
export function normaliseSource(source: WireSource): SourceState {
  const kind = source.kind ?? 'credentials'
  if (kind === 'credentials' || kind === 'collection' || kind === 'consent') {
    return { ...source, kind } as unknown as SourceState
  }
  return { ...source, kind: 'unknown', reportedKind: kind } as unknown as UnknownSource
}

export function normaliseSources(response: { sources?: WireSource[] }): SourcesResponse {
  return { sources: (response.sources ?? []).map(normaliseSource) }
}

/**
 * What is honestly known about a credential's age, and no more.
 *
 * Shared between Sources and Health so the two pages say exactly the same
 * thing about a `credentialConfirmedAt` — currently only IBKR, whose own
 * answer to a proved Flex token names no lifetime, and whose six-hour default
 * can be overridden up to a year by whoever requested the token, a fact this
 * dashboard is never told. Recording and saying the date is the honest middle
 * between silence and a guessed expiry.
 */
export function confirmedAtNote(confirmedAt: string): string {
  return `Confirmed ${confirmedAt.slice(0, 10)}. This dashboard is not told how long that credential is good for, so there is no countdown for it.`
}

export function useSources(): UseQueryResult<SourcesResponse> {
  return useQuery({
    queryKey: SOURCES_KEY,
    queryFn: async () => normaliseSources(await apiFetch<{ sources?: WireSource[] }>('/sources')),
  })
}

const IMPORTS_KEY = ['sources', 'imports'] as const

/**
 * Every run a connected source has answered, most recent first — completed
 * imports rather than the reconstruction still filling one in, which is
 * `useCollectionStanding`'s `history` and arrives on its own clock.
 */
export function useImportHistory(): UseQueryResult<ImportRecord[]> {
  return useQuery({
    queryKey: IMPORTS_KEY,
    queryFn: async () => (await apiFetch<{ imports: ImportRecord[] }>('/sources/imports')).imports,
  })
}

/**
 * Connecting a source changes what every figure is made of, so the whole cache
 * is invalidated rather than just this page — a dashboard still showing the
 * total from before a bank was added is showing a number nobody can explain.
 */
function useSourceMutation<Input, Output>(
  mutationFn: (input: Input) => Promise<Output>,
): UseMutationResult<Output, Error, Input> {
  const client = useQueryClient()
  const remember = useRememberCollection()
  return useMutation({
    mutationFn,
    onSuccess: async (output) => {
      // Recorded first: the refresh below refetches every query, the standing
      // included, and the one that says a collection is queued is the one the
      // page has to be watching for its figures to refresh when it finishes.
      remember(output)
      await client.invalidateQueries()
    },
  })
}

const at = (id: string): string => `/sources/${encodeURIComponent(id)}`

/**
 * What a write that connected something answers with, beyond the sources.
 *
 * Connecting collects that source, and the page that says so is the page
 * somebody is looking at while it happens. The sentence is the server's, so
 * that what is claimed here and what was actually asked for cannot drift.
 */
export interface Connected {
  collection?: ConnectedCollection
}

export function useSaveSource(
  id: string,
): UseMutationResult<Connected, Error, Record<string, string>> {
  return useSourceMutation((values: Record<string, string>) =>
    apiFetch<Connected>(at(id), { method: 'PUT', body: JSON.stringify({ values }) }),
  )
}

/**
 * What is to become of the figures a source produced, once it is disconnected.
 *
 * There is no default here and none on the route either. Keeping is defensible
 * — a broker that reported last year is part of the record of what was true —
 * and so is deleting, because it was that broker's data. The person closing the
 * account is the one who knows, and they are standing right there.
 */
export type SourceHistory = 'retain' | 'delete'

export interface DisconnectOutcome {
  /** Rows removed, by table. Empty where the figures were kept. */
  removed: Record<string, number>
}

export function useDisconnectSource(
  id: string,
): UseMutationResult<DisconnectOutcome, Error, SourceHistory> {
  return useSourceMutation((history: SourceHistory) =>
    apiFetch<DisconnectOutcome>(`${at(id)}?data=${history}`, { method: 'DELETE' }),
  )
}

const connectionAt = (id: string, connectionId: number): string =>
  `${at(id)}/connections/${connectionId}`

export interface NewConnection {
  label: string
  values: Record<string, string>
}

export interface AddedConnection extends Connected {
  connection: { id: number; label: string; createdAt: string }
}

/** A fresh login for a source that holds several — Nordnet, IBKR, Coinbase. */
export function useAddConnection(id: string): UseMutationResult<AddedConnection, Error, NewConnection> {
  return useSourceMutation((input: NewConnection) =>
    apiFetch<AddedConnection>(`${at(id)}/connections`, { method: 'POST', body: JSON.stringify(input) }),
  )
}

export interface RenewConnection {
  label?: string
  values?: Record<string, string>
}

/** A connection's credential renewed, its label changed, or both. */
export function useRenewConnection(
  id: string,
  connectionId: number,
): UseMutationResult<Connected, Error, RenewConnection> {
  return useSourceMutation((input: RenewConnection) =>
    apiFetch<Connected>(connectionAt(id, connectionId), { method: 'PUT', body: JSON.stringify(input) }),
  )
}

/** As `useDisconnectSource`, scoped to one connection rather than a whole source. */
export function useDisconnectConnection(
  id: string,
  connectionId: number,
): UseMutationResult<DisconnectOutcome, Error, SourceHistory> {
  return useSourceMutation((history: SourceHistory) =>
    apiFetch<DisconnectOutcome>(`${connectionAt(id, connectionId)}?data=${history}`, { method: 'DELETE' }),
  )
}

export interface CheckInput {
  kind?: string
  values: Record<string, string>
  /** A stored connection whose own credential fills any box left empty. */
  connectionId?: number
}

/**
 * What the provider says about values nobody has stored yet.
 *
 * Nothing local is invalidated on the way back, because nothing has changed: a
 * check is a question put to somebody else's API, and a page that refetched
 * itself afterwards would suggest it had done something here.
 */
export function useCheckSource(id: string): UseMutationResult<SourceCheck, Error, CheckInput> {
  return useMutation({
    mutationFn: (input: CheckInput) =>
      apiFetch<SourceCheck>(`${at(id)}/check`, { method: 'POST', body: JSON.stringify(input) }),
  })
}

export interface AddItemInput {
  kind?: string
  values: Record<string, string>
}

export function useAddItem(id: string): UseMutationResult<Connected, Error, AddItemInput> {
  return useSourceMutation((input: AddItemInput) =>
    apiFetch<Connected>(`${at(id)}/items`, { method: 'POST', body: JSON.stringify(input) }),
  )
}

export function useRemoveItem(id: string): UseMutationResult<unknown, Error, string> {
  return useSourceMutation((itemId: string) =>
    apiFetch(`${at(id)}/items/${encodeURIComponent(itemId)}`, { method: 'DELETE' }),
  )
}

export interface SetItemEntityInput {
  itemId: string
  entityKey: string
}

/**
 * Reattributing a watched item without touching whether it is watched at all.
 *
 * A separate mutation from adding one, because re-pointing an existing wallet
 * at a different entity is answering "whose is this", not "watch this too" —
 * and the one thing this deliberately cannot do is remove an attribution once
 * given, the same way the account attribution above never defaults either.
 */
export function useSetItemEntity(id: string): UseMutationResult<Connected, Error, SetItemEntityInput> {
  return useSourceMutation((input: SetItemEntityInput) =>
    apiFetch<Connected>(`${at(id)}/items/${encodeURIComponent(input.itemId)}`, {
      method: 'PUT',
      body: JSON.stringify({ values: { entityKey: input.entityKey } }),
    }),
  )
}

export interface AddEntryInput {
  itemId: string
  values: Record<string, string>
}

export function useAddEntry(id: string): UseMutationResult<unknown, Error, AddEntryInput> {
  return useSourceMutation((input: AddEntryInput) =>
    apiFetch(`${at(id)}/items/${encodeURIComponent(input.itemId)}/entries`, {
      method: 'POST',
      body: JSON.stringify({ values: input.values }),
    }),
  )
}

export interface RemoveEntryInput {
  itemId: string
  entryId: string
}

export function useRemoveEntry(id: string): UseMutationResult<unknown, Error, RemoveEntryInput> {
  return useSourceMutation((input: RemoveEntryInput) =>
    apiFetch(
      `${at(id)}/items/${encodeURIComponent(input.itemId)}/entries/${encodeURIComponent(input.entryId)}`,
      { method: 'DELETE' },
    ),
  )
}

/**
 * The banks of one country, fetched when a country other than the one the page
 * arrived with is chosen.
 *
 * The page already carries its opening country's list, so that one is handed
 * back from `initial` rather than fetched a second time — a household that
 * never leaves its own market makes no request at all, and the menu does not
 * blank on first paint.
 *
 * Kept for the session: the banks of a country change on the provider's clock,
 * not on this household's, and a household comparing two markets should not pay
 * a round trip each time it looks back.
 */
export function useBanksIn(
  id: string,
  country: string,
  initial: { country: string; banks: readonly BankOption[] },
): UseQueryResult<readonly BankOption[]> {
  return useQuery({
    queryKey: ['sources', id, 'banks', country] as const,
    queryFn: async () =>
      (
        await apiFetch<{ banks: BankOption[] }>(
          `${at(id)}/banks?country=${encodeURIComponent(country)}`,
        )
      ).banks,
    ...(country === initial.country ? { initialData: initial.banks } : {}),
    staleTime: Infinity,
  })
}

export interface StartConsentInput {
  bank: string
  country: string
  psuType: string
}

export interface StartedConsent {
  url: string
}

/**
 * Where the browser goes next belongs to the bank, so it is a navigation rather
 * than a request. Held on an object the tests replace, because jsdom will not
 * leave the page and a spy on `location` is a fight with the environment.
 */
export const navigation = {
  leaveForBank(url: string): void {
    window.location.assign(url)
  },
}

export function useStartConsent(
  id: string,
): UseMutationResult<StartedConsent, Error, StartConsentInput> {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: StartConsentInput) =>
      apiFetch<StartedConsent>(`${at(id)}/consents`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: (started) => {
      // Nothing local is refreshed: the consent does not exist until the bank
      // says so, and this tab is about to stop being this page.
      client.removeQueries({ queryKey: SOURCES_KEY })
      navigation.leaveForBank(started.url)
    },
  })
}

export interface AccountAttribution {
  uid: string
  entityKey?: string
  excluded: boolean
}

export interface AttributeInput {
  reference: string
  accounts: AccountAttribution[]
}

export function useAttributeAccounts(
  id: string,
): UseMutationResult<Connected, Error, AttributeInput> {
  return useSourceMutation((input: AttributeInput) =>
    apiFetch<Connected>(`${at(id)}/consents/${encodeURIComponent(input.reference)}/accounts`, {
      method: 'PUT',
      body: JSON.stringify({ accounts: input.accounts }),
    }),
  )
}

/** The answer chosen for an account, before it is an answer. */
export type AttributionChoice = string | 'excluded' | ''

export const DONT_TRACK = 'excluded'

export function attributionOf(choice: AttributionChoice, uid: string): AccountAttribution {
  if (choice === DONT_TRACK) return { uid, excluded: true }
  return { uid, entityKey: choice, excluded: false }
}

/**
 * Whether every returned account has been answered. Unanswered is the starting
 * state and stays until somebody chooses, because an account attributed to the
 * wrong entity is taxed at the wrong rate and `lookThrough` — the figure a sole
 * owner reads — is unchanged either way, so the mistake hides behind a figure
 * that still looks right.
 */
export function isFullyAttributed(
  accounts: readonly { uid: string }[],
  choices: Readonly<Record<string, AttributionChoice>>,
): boolean {
  return accounts.length > 0 && accounts.every((account) => (choices[account.uid] ?? '') !== '')
}

/**
 * Why a draft cannot be added yet, in the words of the person adding it, or
 * nothing when it can.
 *
 * The menu is one of the answers, and it is unanswered until somebody answers
 * it. For a wallet that only says which coin; for a private holding the same
 * menu says whose money bought it, and a defaulted answer there is the bug
 * PRO-146 names in a different corner.
 *
 * A field the source marks `single` is one thing and never a list. A
 * comma-separated paste into one is refused rather than split: splitting
 * restores the box this page exists to remove, and it restores it invisibly —
 * a stray comma inside an address becomes two entries, one of them nonsense,
 * reported as a success. A duplicate is refused for a harder reason, that the
 * same wallet counted twice is real money counted twice.
 */
export function refuseDraft(
  shape: AddShape,
  kind: string,
  draft: Readonly<Record<string, string>>,
  taken: readonly string[],
): string | undefined {
  if (shape.kinds !== undefined && kind === '') {
    return `${shape.kindLabel ?? 'That'} has to be answered.`
  }

  for (const field of shape.fields) {
    const value = (draft[field.key] ?? '').trim()
    if (field.required && value === '') return `${field.label} is required.`
    if (value === '' || field.single !== true) continue
    if (/[,;\s]/.test(value)) return `Add one ${field.label.toLowerCase()} at a time.`
    if (taken.some((current) => current.toLowerCase() === value.toLowerCase())) {
      return `That ${field.label.toLowerCase()} is already on the list.`
    }
  }
  return undefined
}

/**
 * The consent kinds a chosen bank actually grants.
 *
 * Read from the bank rather than from a pair of literals on the page: a kind
 * the bank does not offer is refused, and the page that offered it has spent
 * somebody's time on a choice that could never work.
 */
export function holdersOf(
  banks: readonly ConsentSource['banks'][number][],
  bank: string,
): readonly string[] {
  return banks.find((candidate) => candidate.name === bank)?.psuTypes ?? []
}

export function bankNamed(
  banks: readonly ConsentSource['banks'][number][],
  bank: string,
): ConsentSource['banks'][number] | undefined {
  return banks.find((candidate) => candidate.name === bank)
}
